import { isLayoutComponent, isLayoutSection } from '@shared/scenes/workflows/utils';
import {
  AnyLayoutItem,
  ViewTemplateLayoutImage,
  ViewTemplateLayoutText,
  ViewTemplateLayoutVideo,
  isLayoutImageItem,
  isLayoutTextItem,
  isLayoutVideoItem,
} from '@shared/scenes/workflows/viewTemplateLayoutItems';
import { API } from '@shared/types/api';

/**
 * One renderable slot in a subform, resolved from its view template layout.
 *
 * A layout holds fields, sections, and the text and media content items — see
 * `viewTemplateLayoutItems`, which owns the guards and accessors for the latter.
 */
export interface SubformComponentEntry {
  kind: 'component';
  /** Stable across renders, and unique among entries. */
  key: string;
  component: API.AnyComponent;
}

export interface SubformTextEntry {
  kind: 'text';
  key: string;
  item: ViewTemplateLayoutText;
}

export interface SubformImageEntry {
  kind: 'image';
  key: string;
  item: ViewTemplateLayoutImage;
}

export interface SubformVideoEntry {
  kind: 'video';
  key: string;
  item: ViewTemplateLayoutVideo;
}

export type SubformLayoutEntry =
  | SubformComponentEntry
  | SubformTextEntry
  | SubformImageEntry
  | SubformVideoEntry;

export function isSubformComponentEntry(entry: SubformLayoutEntry): entry is SubformComponentEntry {
  return entry.kind === 'component';
}

/**
 * The subform's slots in layout order.
 *
 * Sections are flattened: a subform cannot be given one in the builder, but a layout could
 * still carry one, and skipping it would drop its fields out of the form. Components the
 * layout never mentions — added since it was last written — follow in workflow template order
 * rather than going missing.
 */
export function buildSubformLayoutEntries(
  components: API.AnyComponent[],
  layout?: API.ViewTemplateLayoutFragment | null,
): SubformLayoutEntry[] {
  const componentsById = new Map(components.map(component => [component.id, component]));
  const entries: SubformLayoutEntry[] = [];
  const placed = new Set<string>();

  const addComponent = (componentId: string | null | undefined) => {
    if (!componentId || placed.has(componentId)) {
      return;
    }
    const component = componentsById.get(componentId);
    if (!component) {
      return;
    }
    placed.add(componentId);
    entries.push({ kind: 'component', key: component.id, component });
  };

  // Content items have no component to key on and their own id is optional, so position
  // stands in. Ordinal rather than array index, so a key survives a field being removed
  // from somewhere else in the list.
  let contentItemCount = 0;
  const contentKey = (item: AnyLayoutItem) => {
    contentItemCount += 1;
    return `${item.type}-${item.id ?? contentItemCount}`;
  };

  for (const item of layout?.items ?? []) {
    if (isLayoutComponent(item)) {
      addComponent(item.componentId);
    } else if (isLayoutSection(item)) {
      item.componentIds?.forEach(addComponent);
    } else if (isLayoutTextItem(item)) {
      entries.push({ kind: 'text', key: contentKey(item), item });
    } else if (isLayoutImageItem(item)) {
      entries.push({ kind: 'image', key: contentKey(item), item });
    } else if (isLayoutVideoItem(item)) {
      entries.push({ kind: 'video', key: contentKey(item), item });
    }
  }

  components.forEach(component => addComponent(component.id));

  return entries;
}
